<template>
    <div>
        <page-header>
            API Auth
        </page-header>
        <page-controls>
            <template v-slot:left>
                <vx-button-group class="w-full" @click.prevent="fetchParams.forward = !fetchParams.forward; applyFilters()" size="lg">
                    <vx-button
                        @click.prevent
                        class="grow @2xl/controls:grow-0"
                        type="button"
                        color="white"
                        shadow
                    >
                        <span class="font-normal">Sort: Created at</span>
                    </vx-button>
                    <vx-button
                        @click.prevent
                        type="button"
                        color="white"
                        shadow
                    >
                        <font-awesome-icon widthAuto
                            :icon="
                                fetchParams.forward
                                    ? faArrowUpWideShort
                                    : faArrowDownWideShort
                            "
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </template>
            <template v-slot:right>
                <vx-popover
                    class="w-full"
                    hover
                    :arrow="true"
                    placement="left">
                    <vx-button block
                        :disabled="!isAdminOrOwner"
                        size="lg"
                        color="primary"
                        @click="showAuthConfigTypeModal = true"
                    >
                        New API Auth
                        <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>

                    <template v-if="!isAdminOrOwner" v-slot:content>
                        You do not have permission to create.
                    </template>
                </vx-popover>
            </template>
        </page-controls>

        <vx-alert v-if="recentlyCreated" color="success" :closeable="false" shadow class="my-12">
            <template v-slot:icon>
                <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
            </template>
            <strong>{{ list[0].friendly_name }}</strong> created successfully
        </vx-alert>

        <page-loading v-if="loading && !list.length"></page-loading>

        <template v-if="!loading && !list.length">
            <vx-box color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                No API Auths yet.
                <vx-popover
                    hover
                    :arrow="true"
                    placement="left">
                    <vx-button :disabled="!isAdminOrOwner"
                        size="lg"
                        color="primary"
                        @click="showAuthConfigTypeModal = true"
                    >
                        New API Auth
                        <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>

                    <template v-if="!isAdminOrOwner" v-slot:content>
                        You do not have permission to create.
                    </template>
                </vx-popover>
            </vx-box>
        </template>

        <vx-table v-else-if="list.length" class="lg:grid-cols-2 xl:grid-cols-5-auto" rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div>
                    Name
                </div>
                <div>
                    Type
                </div>
                <div>
                    Destinations
                </div>
                <div>
                    Created at
                </div>
            </vx-table-row>

            <auth-config-row v-for="(item, index) in list" :item="list[index]" :key="list[index].id" @deleted="removeFromList($event)"></auth-config-row>

        </vx-table>

        <div
            v-if="list.length"
            class="mt-8 flex justify-between w-full gap-10"
        >
            <vx-button
                v-if="fetchParams.cursor"
                @click="fetchRecords()"
                color="muted-dark"
                size="xl"
                class="grow-0"
                :loading="loading"
            >
                Load More
                <font-awesome-icon widthAuto
                    :icon="faSpinner"
                ></font-awesome-icon>
            </vx-button>
        </div>
        <auth-config-type-modal v-model:visible="showAuthConfigTypeModal" @continue="newAuthConfig($event)"></auth-config-type-modal>
    </div>
</template>


<script setup>
import PageHeader from '../../../general/PageHeader.vue';
import PageControls from '../../../general/PageControls.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCirclePlus, faArrowUpWideShort, faArrowDownWideShort, faSpinner, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { VxButtonGroup, VxButton, VxBox, VxTable, VxTableRow, VxPopover, VxAlert } from '@voxie/frontend-components';
import { onMounted, ref, computed } from 'vue';
import AuthConfigRow from './AuthConfigRow.vue';
import { useToasted } from '../../../../composables/useToasted';
import { authConfigsClient } from '~/services';
import parseLinkHeader from '~/utils/parseLinkHeader'
import AuthConfigTypeModal from '~/components/settings/integrations/auth-configs/AuthConfigTypeModal.vue';
import dayjs  from '~/utils/dayjs';
import PageLoading from '~/components/general/PageLoading.vue';

const teamId = Spark.state.currentTeam.id;
const isAdminOrOwner = Spark.isAdminOrOwner;
const toasted = useToasted();
const showAuthConfigTypeModal = ref(false);

const defaultFilters = () => ({
    limit: 10,
    cursor: undefined,
    forward: false,
});

const list = ref([]);
const loading = ref(false);

const fetchParams = ref({});

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    fetchRecords();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: undefined,
    }

    fetchRecords();
}

onMounted(() => {
    resetFilters();
});

const fetchRecords = async () => {
    if (loading.value) {
        return;
    }

    loading.value = true;
    try {
        const response = await authConfigsClient.list(teamId, {
            ...fetchParams.value,
        });

        list.value =
            !fetchParams.value?.cursor
                ? response.data.data
                : [...list.value, ...response.data.data];

        const nextLink = parseLinkHeader(response.headers?.link)?.[fetchParams.value.forward ? 'next' : 'prev'];
        const cursor = nextLink?.cursor;

        fetchParams.value = {
            ...fetchParams.value,
            cursor,
        }
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.auth_config_id != itemId);

    if (!list.value.length) {
        fetchRecords();
    }
};

const recentlyCreated = computed(() =>
    list.value[0]?.created_at
        ? dayjs.utc(list.value[0].created_at).isAfter(dayjs().utc().subtract(15, 'seconds'))
        : false
);

const newAuthConfig = (authConfigType) => {
    window.location.assign(`/settings/integrations/auth-configs/new?auth_config_type=${authConfigType}`);
}

</script>
